/**
 * PageHeader
 *
 * Standard Bloom-styled banner used at the top of every main page. Mirrors the service
 * session header: the Bloom Bright logo mark is reused as a tinted watermark, coloured to
 * the page's accent, with an icon badge, title and optional subtitle.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const BLOOM_TAN = '#daab84';

// The Bloom Bright mark (outer swoosh + inner "bloom" petals), reused as a watermark.
const BLOOM_MARK = (
    <>
        <path transform="translate(51.16)" d="M41,115.3a3.49,3.49,0,1,1-3.31,6.15,54.66,54.66,0,0,1-7.41-4.76A29.4,29.4,0,0,1,23.79,110c-2.5-3.72-7.21-11.36-11.56-18.44C9,86.34,6,81.41,4.38,78.94A27.67,27.67,0,0,1,0,62.92,38,38,0,0,1,5.18,45.54c2.21-3.92,3.62-6.38,5.05-8.89,3.65-6.4,7.46-13.08,9.38-16.71A37.08,37.08,0,0,1,33.38,5.09,38,38,0,0,1,53.11,0H84.24C93,0,99.47,2.18,104.84,6.18c5.22,3.89,9.2,9.37,13.23,16.19l10.39,17.7c5.54,9.44,7.87,15.6,7.63,22.08s-3,12.51-7.62,22c-1.32,2.7-5.39,9.89-9.6,16.73a135.86,135.86,0,0,1-8.4,12.46c-2.28,2.83-5.88,4.9-9.13,6.78l-.2.11c-.82.47-1.62.94-2.46,1.46A3.48,3.48,0,1,1,95,115.8c.74-.47,1.69-1,2.69-1.6l.18-.11c2.72-1.57,5.74-3.32,7.19-5.11a129.11,129.11,0,0,0,7.91-11.76c4-6.54,8-13.48,9.24-16.13,4.24-8.68,6.75-14.17,6.93-19.18s-1.85-10.1-6.67-18.31l-5.36-9.14c-1.3-2.21-3-5.17-5-8.53-3.6-6.1-7.07-10.94-11.37-14.15S91.38,7,84.24,7H53.11a31.13,31.13,0,0,0-16.18,4.11A30.25,30.25,0,0,0,25.79,23.22c-1.71,3.23-5.68,10.2-9.49,16.87C14.12,43.91,12,47.65,11.25,49A31.21,31.21,0,0,0,6.94,63.11a20.8,20.8,0,0,0,3.32,12c1.94,3,4.84,7.74,7.93,12.76,4.44,7.21,9.24,15,11.39,18.21a22.82,22.82,0,0,0,4.94,5A48.77,48.77,0,0,0,41,115.3" />
        <path transform="translate(51.16)" fillRule="evenodd" d="M40.14,66.85V82.73A11.16,11.16,0,0,0,51.25,93.84H66.4V79.4A12.6,12.6,0,0,0,53.85,66.85Zm32.7,26.8H87.11a8.41,8.41,0,0,0,8.37-8.37V76.66H80.89c-4.75,0-8,3.85-8,8.47ZM52.56,118.29h14V99.56H51.25A16.87,16.87,0,0,1,34.42,82.73V63.94a2.82,2.82,0,0,1,2.81-2.81H53.85A18.35,18.35,0,0,1,71.77,75.81a8,8,0,0,1,7.42-5.06H98.52a2.16,2.16,0,0,1,2.15,2.15V85.28A14.33,14.33,0,0,1,86.39,99.56H72.12v18.73h14a2.78,2.78,0,0,1,2.77,2.77h0a2.77,2.77,0,0,1-2.77,2.76H52.56a2.77,2.77,0,0,1-2.77-2.76h0A2.78,2.78,0,0,1,52.56,118.29Z" />
        <path transform="translate(51.16)" fillRule="evenodd" d="M58.5,128.73H80.22A2.77,2.77,0,0,1,83,131.49h0a2.77,2.77,0,0,1-2.76,2.77H58.5a2.78,2.78,0,0,1-2.77-2.77h0a2.77,2.77,0,0,1,2.77-2.76" />
        <path transform="translate(51.16)" fillRule="evenodd" d="M86.87,35.72H78.71a8.2,8.2,0,0,0-8.15,8.16v9.53h8.15a8.19,8.19,0,0,0,8.16-8.15Zm2.19-5.65H78.71A13.85,13.85,0,0,0,64.9,43.88V55.6a3.48,3.48,0,0,0,3.47,3.47H78.71A13.86,13.86,0,0,0,92.53,45.26V33.54A3.48,3.48,0,0,0,89.06,30.07Z" />
    </>
);

const withAlpha = (hex, a) => {
    if (typeof hex !== 'string' || hex[0] !== '#' || hex.length !== 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const PageHeader = ({ title, subtitle, icon: Icon, accent = '#8e8741', rightContent }) => (
    <Box
        sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            px: 3,
            py: rightContent ? 1.25 : 1,
            mb: 2,
            background: `linear-gradient(120deg, ${withAlpha(accent, 0.16)} 0%, ${withAlpha(BLOOM_TAN, 0.32)} 100%)`,
            //borderBottom: `3px solid ${accent}`,
        }}
    >
        {/* Bloom mark watermark, tinted to the page accent */}
        <Box
            component="svg"
            viewBox="40 -6 160 150"
            aria-hidden
            sx={{ position: 'absolute', right: -18, top: '50%', transform: 'translateY(-50%) rotate(8deg)', width: 140, height: 140, opacity: 0.16, pointerEvents: 'none', fill: accent }}
        >
            {BLOOM_MARK}
        </Box>

        {Icon && (
            <Box
                sx={{
                    zIndex: 1, flexShrink: 0,
                    width: 46, height: 46, borderRadius: '50%',
                    bgcolor: '#fff', border: `2px solid ${accent}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}
            >
                <Icon sx={{ color: accent, fontSize: 25 }} />
            </Box>
        )}

        <Box sx={{ zIndex: 1, minWidth: 0, pr: rightContent ? 2 : 4 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#2c2a1e', lineHeight: 1.2 }} noWrap title={title}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 12.5, color: '#6b6a52', fontWeight: 600, mt: 0.25 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>

        {/* Optional right-aligned content (e.g. compact metric cards) — sits in the header row so
            the space next to the title is used instead of a separate band below it. */}
        {rightContent && (
            <Box sx={{ zIndex: 1, ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {rightContent}
            </Box>
        )}
    </Box>
);

export default PageHeader;
